<template>
  <div class="clipboard-composer">
    <div class="clipboard-composer__row">
      <select
        :value="effectiveType"
        class="clipboard-composer__type-select"
        aria-label="Item type"
        @change="onTypeChange"
      >
        <option
          v-for="t in typeOptions"
          :key="t.value"
          :value="t.value"
        >
          {{ t.label }}
        </option>
      </select>
      <input
        :value="form.title"
        type="text"
        class="clipboard-composer__label-input"
        :placeholder="config.labelPlaceholder"
        maxlength="255"
        @input="onTitleInput"
      >
    </div>

    <button
      v-if="config.bulkPaste"
      type="button"
      class="clipboard-composer__bulk-toggle"
      @click="toggleBulkPaste"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <rect
          width="18"
          height="18"
          x="3"
          y="3"
          rx="2"
        />
        <path d="M21 9H3" />
        <path d="M21 15H3" />
      </svg>
      {{ bulkPaste ? 'Bulk paste on' : 'Bulk paste off' }}
    </button>

    <textarea
      v-if="useMultiline"
      ref="contentField"
      :value="form.content"
      class="clipboard-composer__content clipboard-composer__content--area"
      :placeholder="contentPlaceholder"
      maxlength="10000"
      rows="3"
      @input="onContentInput"
      @keydown.enter.exact.prevent="submitOnEnter"
    />
    <input
      v-else
      ref="contentField"
      :value="form.content"
      type="text"
      class="clipboard-composer__content clipboard-composer__content--input"
      :class="{ 'clipboard-composer__content--mono': config.mono }"
      :placeholder="contentPlaceholder"
      maxlength="10000"
      @input="onContentInput"
      @keydown.enter.prevent="submitOnEnter"
    >

    <div class="clipboard-composer__actions">
      <button
        type="button"
        class="clipboard-composer__btn clipboard-composer__btn--ghost"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
      <button
        type="button"
        class="clipboard-composer__btn clipboard-composer__btn--primary"
        :disabled="!canSave || saving"
        @click="$emit('save')"
      >
        <span v-if="saving">Saving…</span>
        <span v-else>Save</span>
      </button>
    </div>
  </div>
</template>

<script>
import {
  CLIPBOARD_COMPOSER_CONFIG,
  CLIPBOARD_TYPES,
  normalizeClipboardType,
} from './clipboard-constants';

// Cap the auto-grown content textarea so a very long prompt doesn't take over the whole
// drawer; beyond this it scrolls. Typical prompts fit fully. (POR-13563-2)
const MAX_CONTENT_HEIGHT = 360;

export default {
  name: 'ClipboardComposer',

  props: {
    form: {
      type: Object,
      required: true,
    },
    saving: {
      type: Boolean,
      default: false,
    },
    bulkPaste: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      typeOptions: CLIPBOARD_TYPES,
    };
  },

  computed: {
    effectiveType() {
      return normalizeClipboardType(this.form.type);
    },

    config() {
      return CLIPBOARD_COMPOSER_CONFIG[this.effectiveType] || CLIPBOARD_COMPOSER_CONFIG.prompt;
    },

    useMultiline() {
      if (this.config.bulkPaste && this.bulkPaste) {
        return true;
      }
      return this.config.multiline;
    },

    contentPlaceholder() {
      if (this.config.bulkPaste && this.bulkPaste) {
        return this.config.bulkPlaceholder;
      }
      return this.config.contentPlaceholder;
    },

    canSave() {
      return Boolean(String(this.form.content || '').trim());
    },
  },

  watch: {
    'form.type': {
      immediate: true,
      handler(newType, oldType) {
        const normalized = normalizeClipboardType(newType);
        if (normalized !== newType) {
          this.patchForm({ type: normalized });
          return;
        }
        if (newType !== oldType) {
          this.$emit('update:bulkPaste', false);
        }
      },
    },
    // When the multiline textarea appears (type switched to prompt/note, or bulk paste
    // toggled), size it to its current content. (POR-13563-2)
    useMultiline() {
      this.$nextTick(() => this.autoGrowContent());
    },
  },

  mounted() {
    // The composer mounts fresh each time it opens — grow to fit any pre-filled content
    // (e.g. "Save current" with a long prompt) so the whole thing is visible instead of a
    // fixed 3-row box scrolled to the bottom. (POR-13563-2)
    this.$nextTick(() => this.autoGrowContent());
  },

  methods: {
    onTypeChange(event) {
      this.patchForm({ type: normalizeClipboardType(event.target.value) });
      this.$emit('update:bulkPaste', false);
    },

    onTitleInput(event) {
      this.patchForm({ title: event.target.value });
    },

    onContentInput(event) {
      this.patchForm({ content: event.target.value });
      this.autoGrowContent();
    },

    // Auto-size the multiline content field to fit its text (capped, then it scrolls) so a
    // long prompt is fully visible. No-op for the single-line <input> variants. (POR-13563-2)
    autoGrowContent() {
      const el = this.$refs.contentField;
      if (!el || el.tagName !== 'TEXTAREA') {
        return;
      }
      el.style.height = 'auto';
      // scrollHeight is the content+padding box; with box-sizing:border-box the element
      // height must also include the borders, otherwise it lands ~2px short and shows a
      // tiny residual scroll. (POR-13563-2)
      const borders = el.offsetHeight - el.clientHeight;
      el.style.height = `${Math.min(el.scrollHeight + borders, MAX_CONTENT_HEIGHT)}px`;
    },

    patchForm(partial) {
      this.$emit('update:form', { ...this.form, ...partial });
    },

    toggleBulkPaste() {
      this.$emit('update:bulkPaste', !this.bulkPaste);
    },

    submitOnEnter() {
      if (!this.canSave || this.saving) {
        return;
      }
      this.$emit('save');
    },

    focusContent() {
      this.$refs.contentField?.focus();
    },
  },
};
</script>

<style scoped>
.clipboard-composer {
  flex-shrink: 0;
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: rgba(243, 244, 246, 0.45);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clipboard-composer__row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.clipboard-composer__type-select {
  width: 110px;
  height: 32px;
  flex-shrink: 0;
  font-size: 0.75rem;
  padding: 0 28px 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E") no-repeat right 8px center;
  appearance: none;
  color: #111827;
}

.clipboard-composer__type-select:focus {
  outline: none;
  border-color: #9ca3af;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.08);
}

.clipboard-composer__label-input {
  flex: 1;
  min-width: 0;
  height: 32px;
  font-size: 0.75rem;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #111827;
}

.clipboard-composer__label-input::placeholder {
  color: #9ca3af;
}

.clipboard-composer__label-input:focus {
  outline: none;
  border-color: #9ca3af;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.08);
}

.clipboard-composer__bulk-toggle {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  line-height: 1.2;
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #6b7280;
  transition: color 120ms ease, border-color 120ms ease;
}

.clipboard-composer__bulk-toggle:hover {
  color: #111827;
  border-color: #d1d5db;
}

.clipboard-composer__content {
  width: 100%;
  font-size: 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #fff;
  color: #111827;
}

.clipboard-composer__content--area {
  min-height: 64px;
  max-height: 360px;
  line-height: 1.45;
  padding: 8px 10px;
  resize: vertical;
  overflow-y: auto;
  box-sizing: border-box;
}

.clipboard-composer__content--input {
  height: 32px;
  padding: 0 10px;
}

.clipboard-composer__content--mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  letter-spacing: 0.05em;
}

.clipboard-composer__content::placeholder {
  color: #9ca3af;
}

.clipboard-composer__content:focus {
  outline: none;
  border-color: #9ca3af;
  box-shadow: 0 0 0 2px rgba(17, 24, 39, 0.08);
}

.clipboard-composer__actions {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
}

.clipboard-composer__btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  transition: background 120ms ease, color 120ms ease, opacity 120ms ease;
}

.clipboard-composer__btn--ghost {
  background: transparent;
  color: #374151;
}

.clipboard-composer__btn--ghost:hover {
  background: #f3f4f6;
}

.clipboard-composer__btn--primary {
  background: #17634a;
  color: #fff;
}

.clipboard-composer__btn--primary:hover:not(:disabled) {
  background: #145a43;
}

.clipboard-composer__btn--primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
